/**
 * SSE (Server-Sent Events) parsing utilities
 */

export const StreamEventType = {
  PROCESSING_START: 'processing_start',
  TEXT_CHUNK: 'text_chunk',
  PROCESSING_END: 'processing_end',
  COMPLETE: 'complete',
  ERROR: 'error',
  HUMAN_DECISION_REQUIRED: 'human_decision_required',
};

/**
 * Parses SSE data payload into a structured stream event
 * @param {string} payload - The SSE data payload
 * @returns {object|null} - Parsed stream event or null if invalid
 */
export const parseSSEData = (payload) => {
  if (!payload || typeof payload !== 'string') {
    return null;
  }

  // Handle control messages
  if (payload === '[DONE]' || payload === '[PROCESSING]') {
    return null;
  }

  try {
    const parsed = JSON.parse(payload);

    // Validate the structure
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    // Map common event types
    const eventType = parsed.type || parsed.event_type;

    // Handle text_chunk events
    if (eventType === StreamEventType.TEXT_CHUNK || parsed.content) {
      // Extract content from either content field or OpenAI format choices[0].delta.content
      const content = parsed.content || parsed.choices?.[0]?.delta?.content || '';
      return {
        type: StreamEventType.TEXT_CHUNK,
        content: content,
        ...parsed,
      };
    }

    // Handle processing_start
    if (eventType === StreamEventType.PROCESSING_START || parsed.type === 'processing_start') {
      return {
        type: StreamEventType.PROCESSING_START,
        ...parsed,
      };
    }

    // Handle processing_end
    if (eventType === StreamEventType.PROCESSING_END || parsed.type === 'processing_end') {
      return {
        type: StreamEventType.PROCESSING_END,
        ...parsed,
      };
    }

    // Handle complete
    if (eventType === StreamEventType.COMPLETE || parsed.type === 'complete') {
      return {
        type: StreamEventType.COMPLETE,
        finalContent: parsed.finalContent || parsed.content || '',
        ...parsed,
      };
    }

    // Handle error
    if (eventType === StreamEventType.ERROR || parsed.type === 'error') {
      return {
        type: StreamEventType.ERROR,
        error: parsed.error || parsed.message || 'Unknown error',
        ...parsed,
      };
    }

    // Handle human_decision_required
    if (eventType === StreamEventType.HUMAN_DECISION_REQUIRED || parsed.type === 'human_decision_required') {
      return {
        type: StreamEventType.HUMAN_DECISION_REQUIRED,
        decisionId: parsed.decisionId || parsed.decision_id,
        question: parsed.question || '',
        options: parsed.options || [],
        context: parsed.context || {},
        ...parsed,
      };
    }

    // Return parsed object with type if it exists
    if (eventType) {
      return {
        type: eventType,
        ...parsed,
      };
    }

    return parsed;
  } catch (e) {
    // Not valid JSON, return null
    return null;
  }
};